import React from 'react';

import axios from 'axios';

import {
  isChunkLoadError,
  RELOAD_FALLBACK_MS,
  reloadOnceForChunkError,
} from 'utils/chunkLoadRecovery';
import { logger } from 'utils/logger';
import { getIsNativePlatform } from 'utils/platform';

import ErrorScreen from './shared/components/common/ErrorScreen';

const DEFAULT_STATE = { error: null, errorInfo: null, isReloading: false };

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = DEFAULT_STATE;
  }

  componentDidMount() {
    window.addEventListener('popstate', this.onPopState);
    window.addEventListener('unhandledrejection', this.onUnhandledRejection);
  }

  onUnhandledRejection = event => {
    if (axios.isCancel(event.reason)) {
      event.preventDefault();
    }
  };

  componentWillUnmount() {
    clearTimeout(this.reloadFallbackTimer);
    window.removeEventListener('popstate', this.onPopState);
    window.removeEventListener('unhandledrejection', this.onUnhandledRejection);
  }

  componentDidCatch(error, errorInfo) {
    // Backstop for chunks loaded outside lazyWithRetry: a stale build recovers by reloading instead
    // of stranding the user on the generic error screen.
    const isReloading = isChunkLoadError(error) && reloadOnceForChunkError();

    this.setState({
      error,
      errorInfo,
      isReloading,
    });

    clearTimeout(this.reloadFallbackTimer);

    if (isReloading) {
      // Falls back to the error screen when the reload was cancelled and this page stayed.
      this.reloadFallbackTimer = setTimeout(
        () => this.setState({ isReloading: false }),
        RELOAD_FALLBACK_MS,
      );
    }

    logger.error(error, errorInfo);
  }

  onPopState = () => {
    if (this.state.error) {
      // Clearing the error state re-renders children (the recovered app) in place. Web additionally
      // reloads to guarantee a clean re-fetch from the server; native has no server to re-fetch from
      // and a reload would tear down the persistent JS context + native SIP registration, so on
      // native the state reset alone is the recovery — no page reload (DEV-4332).
      this.setState(DEFAULT_STATE);

      if (!getIsNativePlatform()) {
        window.location.reload();
      }
    }
  };

  render() {
    if (this.state.isReloading) {
      return null;
    }

    if (this.state.error) {
      return <ErrorScreen />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
